import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Hourglass, ArrowRight, Check, Warning, ArrowLeft } from "@phosphor-icons/react";
import AuthLayout from "../components/AuthLayout";
import { Input, Label } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { api, extractError } from "../lib/api";

/**
 * Iter 19.52 — Coming-Soon-Variante der Registrierung.
 * Wird angezeigt, sobald `registration_disabled = true` ist (Super-Admin-Toggle).
 * Sammelt freiwillige E-Mail-Eintragungen in eine Waitlist, die später vom
 * Admin als CSV exportiert + per Mail benachrichtigt werden kann.
 */
export default function ComingSoonPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | already | error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      const { data } = await api.post("/system/waitlist", { email: email.trim() });
      if (data?.already_signed_up) {
        setStatus("already");
      } else {
        setStatus("success");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(extractError(err) || "Bitte versuchen Sie es später erneut.");
    }
  };

  const isSubmitted = status === "success" || status === "already";

  return (
    <AuthLayout>
      <div className="w-full max-w-md mx-auto" data-testid="coming-soon-page">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 rounded-sm border border-brand-300 bg-brand-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-700 mb-6">
          <Hourglass size={11} weight="fill" />
          Bald verfügbar
        </div>

        {/* Heading */}
        <h1
          className="font-cabinet text-3xl sm:text-4xl tracking-tight text-ink-900 font-medium leading-tight mb-3"
          data-testid="coming-soon-title"
        >
          Die Registrierung öffnet bald<span className="text-brand-500">.</span>
        </h1>
        <p className="text-sm text-warm-700 leading-relaxed mb-8">
          ET — Das Einstufungs-Tool steht kurz vor dem öffentlichen Start.
          Tragen Sie sich ein und wir benachrichtigen Sie per E-Mail, sobald
          die Registrierung freigeschaltet ist.
        </p>

        {/* Form */}
        {!isSubmitted && (
          <form
            onSubmit={submit}
            className="space-y-4 mb-8"
            data-testid="waitlist-form"
            noValidate
          >
            <div>
              <Label htmlFor="waitlist-email">Ihre E-Mail-Adresse</Label>
              <Input
                id="waitlist-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@firma.at"
                data-testid="waitlist-email-input"
                disabled={status === "submitting"}
              />
            </div>
            {status === "error" && (
              <div
                role="alert"
                data-testid="waitlist-error"
                className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
              >
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                <div>{errorMsg}</div>
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={status === "submitting" || !email.trim()}
              data-testid="waitlist-submit"
            >
              {status === "submitting" ? "Wird eingetragen…" : "Benachrichtigen lassen"}
              {status !== "submitting" && <ArrowRight size={14} weight="bold" className="ml-1" />}
            </Button>
            <p className="text-[11px] text-warm-500 leading-relaxed">
              Wir verwenden Ihre E-Mail ausschließlich, um Sie über den Start zu
              informieren. Keine Werbung, kein Newsletter — eine einzige
              Benachrichtigung.
            </p>
          </form>
        )}

        {isSubmitted && (
          <div
            className="rounded-sm border border-brand-300 bg-brand-50 p-5 mb-8"
            data-testid="waitlist-success"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500">
                <Check size={16} weight="bold" className="text-white" />
              </div>
              <div>
                <div className="font-cabinet text-base font-medium text-ink-900 mb-1">
                  {status === "already" ? "Sie sind bereits eingetragen" : "Vielen Dank!"}
                </div>
                <p className="text-sm text-warm-700 leading-relaxed">
                  Wir melden uns bei <strong>{email}</strong>, sobald die
                  Registrierung freigeschaltet ist.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer links */}
        <div className="border-t border-ink-200 pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-warm-700 hover:text-ink-900"
            data-testid="coming-soon-back"
          >
            <ArrowLeft size={14} weight="bold" />
            Zur Startseite
          </Link>
          <a
            href="https://www.vorlagenportal.at"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:text-brand-700"
          >
            www.vorlagenportal.at
          </a>
        </div>
      </div>
    </AuthLayout>
  );
}
